import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Grid,
  CircularProgress,
  Alert,
  Paper,
  Breadcrumbs,
  Link,
  Checkbox,
  Badge,
  styled,
  useTheme,
  useMediaQuery,
  Tooltip,
  Snackbar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FolderIcon from '@mui/icons-material/Folder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { ImageLibraryFolderService, ImageLibraryImageService } from './services/imageLibraryService';
import ImageUploadModal from './ImageUploadModal';
import VideoUploadModal from './VideoUploadModal';
import { useTranslation } from 'react-i18next';

// Interfaces for Folder and Image data
interface Image {
  id: string;
  name: string;
  url: string;
  thumbnailUrl?: string;
  createdAt: string;
  folderId?: string;
  mediaType?: string;
}

interface Folder {
  id: string;
  name: string;
  createdAt: string;
  imageCount?: number;
  color?: string;
}

interface ImageLibrarySelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (selectedUrls: string[]) => void;
  companyId: string | null;
  productName: string;
  currentImageUrls: string[];
  productId: string | null;
  singleSelect?: boolean; // ✅ YENİ: Tek resim seçimi modu
  maxSelection?: number;
  noticeMessage?: string | null; // ✅ YENİ: Üstte gösterilecek bilgilendirme/uyarı mesajı
}

const DEFAULT_MAX_IMAGES = 8;

const StyledPaper = styled(Paper, {
    shouldForwardProp: (prop) => prop !== 'isSelected',
  })<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
    padding: theme.spacing(1),
    textAlign: 'center',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
    border: isSelected ? `3px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[4],
    },
}));

const ImageBox = styled(Box)({
    width: '100%',
    paddingTop: '100%', // 1:1 aspect ratio
    position: 'relative',
    borderRadius: '4px',
    overflow: 'hidden',
    backgroundColor: '#f0f0f0',
});

const StyledImage = styled('img')({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
});

const FolderBox = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
});


const ImageLibrarySelectionModal: React.FC<ImageLibrarySelectionModalProps> = ({
  open,
  onClose,
  onSave,
  companyId,
  productName,
  currentImageUrls,
  productId,
  singleSelect = false, // ✅ YENİ: Varsayılan çoklu seçim
  maxSelection = DEFAULT_MAX_IMAGES,
  noticeMessage = null,
}) => {
  const { t } = useTranslation('common2');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // States to hold all library data
  const [allFolders, setAllFolders] = useState<Folder[]>([]);
  const [allImages, setAllImages] = useState<Image[]>([]);

  // State for navigation
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);

  const [selectedImageUrls, setSelectedImageUrls] = useState<string[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'warning' | 'info' } | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isVideoUploadModalOpen, setIsVideoUploadModalOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Mevcut resmin bulunduğu klasöre yalnızca bir kez otomatik yönlendirme yapmak için
  const autoNavigatedRef = useRef(false);
  // Otomatik olarak görünür alana kaydırılacak resmin DOM referansı ve URL'si
  const targetImageRef = useRef<HTMLDivElement | null>(null);
  const [scrollTargetUrl, setScrollTargetUrl] = useState<string | null>(null);

  useEffect(() => {
    if(open) {
      // When modal opens, set the currently selected images for the product
      setSelectedImageUrls(currentImageUrls);
    } else {
      autoNavigatedRef.current = false;
      setScrollTargetUrl(null);
    }
  }, [open, currentImageUrls]);

  // Modal açıldığında, mevcut (seçili) resmin bulunduğu klasörü otomatik aç
  useEffect(() => {
    if (!open || loading || autoNavigatedRef.current) return;
    autoNavigatedRef.current = true;

    const targetUrl = currentImageUrls?.[0]?.trim();
    if (!targetUrl) return;

    // URL tam eşleşmezse thumbnail ile de dene (boşluk farkları için trim'li karşılaştırma)
    const matchedImage = allImages.find(
      img => img.url?.trim() === targetUrl || img.thumbnailUrl?.trim() === targetUrl
    );
    if (matchedImage) {
      if (matchedImage.folderId) {
        const targetFolder = allFolders.find(f => f.id === matchedImage.folderId);
        if (targetFolder) {
          setSelectedFolder(targetFolder);
        }
      }
      // İlgili klasör açıldıktan sonra bu resmi görünür alana kaydır
      setScrollTargetUrl(matchedImage.url);
    }
  }, [open, loading, allImages, allFolders, currentImageUrls]);

  // Hedef resmi ekranda görünür alana kaydır (çok resimli klasörlerde de görünsün)
  useEffect(() => {
    if (!scrollTargetUrl || loading) return;
    const timer = setTimeout(() => {
      targetImageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setScrollTargetUrl(null);
    }, 250);
    return () => clearTimeout(timer);
  }, [scrollTargetUrl, selectedFolder, loading]);
  
  const loadData = useCallback(async () => {
    if (!open || !companyId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('📂 [SELECTION MODAL] SQL\'den klasörler ve resimler yükleniyor...');
      
      // Klasörleri yükle
      const folders = await ImageLibraryFolderService.getAllFolders(companyId);
      const loadedFolders: Folder[] = folders.map(f => ({
        id: f.id,
        name: f.name,
        createdAt: f.createdAt,
        imageCount: f.imageCount || 0,
        color: f.color,
      }));
      setAllFolders(loadedFolders.sort((a, b) => a.name.localeCompare(b.name)));
      console.log('✅ [SELECTION MODAL] Klasörler yüklendi:', loadedFolders.length);

      // Tüm resimleri yükle
      const images = await ImageLibraryImageService.getAllImages(companyId);
      const loadedImages: Image[] = images.map(img => ({
        id: img.id,
        name: img.name,
        url: img.url,
        thumbnailUrl: img.thumbnailUrl,
        createdAt: img.createdAt,
        folderId: img.folderId || '',
        mediaType: img.mediaType,
      }));
      setAllImages(loadedImages);
      console.log('✅ [SELECTION MODAL] Resimler yüklendi:', loadedImages.length);
      
      setLoading(false);
    } catch (err) {
      console.error('❌ [SELECTION MODAL] Veri yükleme hatası:', err);
      setError(t('imageLibraryModal.errors.loadFailed'));
      setLoading(false);
    }
  }, [open, companyId, t]);

  // Load folders and images from SQL API
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  const handleImageSelect = (url: string) => {
    setSelectedImageUrls(prev => {
      // ✅ Tek seçim modunda
      if (singleSelect) {
        if (prev.includes(url)) {
          setError(null);
          return []; // Seçimi kaldır
        } else {
          setError(null);
          return [url]; // Sadece bu resmi seç
        }
      }
      
      // Çoklu seçim modu
      if (prev.includes(url)) {
        setError(null);
        return prev.filter(u => u !== url);
      } else {
        if (prev.length === maxSelection) {
          setSnackbar({ open: true, message: t('imageLibraryModal.maxSelectionWarning', { max: maxSelection }), severity: 'warning' });
          return prev;
        }
        setError(null);
        return [...prev, url];
      }
    });
  };

  const handleSave = () => {
    onSave(selectedImageUrls);
    handleClose();
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(null);
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
        setSelectedFolder(null); // Reset navigation
        setSelectedImageUrls([]);
        setError(null);
    }, 300);
  };
  
  const handleFolderClick = (folder: Folder) => {
    setSelectedFolder(folder);
  };

  const handleGoBack = () => {
    setSelectedFolder(null);
  };

  // Determine what to display based on navigation state
  const displayFolders = selectedFolder ? [] : allFolders;
  const displayImages = selectedFolder 
    ? allImages.filter(img => img.folderId === selectedFolder.id)
    : allImages.filter(img => !img.folderId);

  // Resim URL helper - thumbnail varsa onu göster, yoksa orijinali
  const getImageDisplayUrl = (image: Image): string => {
    return image.thumbnailUrl || image.url;
  };


  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '90vh' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {selectedFolder && (
                    <IconButton onClick={handleGoBack} sx={{ mr: 1 }}>
                        <ArrowBackIcon />
                    </IconButton>
                )}
                <Box>
                    <Typography variant="h6" component="div">
                      {selectedFolder ? selectedFolder.name : t('imageLibraryModal.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">{productName}</Typography>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddPhotoAlternateIcon />}
                onClick={() => setIsUploadModalOpen(true)}
                disabled={!companyId}
              >
                {t('imageLibraryModal.actions.uploadNewImage')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<VideoLibraryIcon />}
                onClick={() => setIsVideoUploadModalOpen(true)}
                disabled={!companyId}
              >
                {t('imageLibraryModal.actions.uploadNewVideo', { defaultValue: 'Video Yükle (MP4)' })}
              </Button>
              <IconButton onClick={handleClose}>
                  <CloseIcon />
              </IconButton>
            </Box>
        </DialogTitle>
        <DialogContent dividers>
            {noticeMessage && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {noticeMessage}
                </Alert>
            )}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert severity="error">{error}</Alert>
            ) : (
                <Grid container spacing={2}>
                    {displayFolders.map(folder => (
                        <Grid item key={folder.id} xs={6} sm={4} md={3} lg={2}>
                             <Tooltip title={t('imageLibraryModal.folderTooltip', { name: folder.name, count: folder.imageCount || 0 })} placement="top">
                                <StyledPaper onClick={() => handleFolderClick(folder)}>
                                    <FolderBox sx={{height: isMobile ? '120px' : '150px'}}>
                                        <FolderIcon sx={{ fontSize: isMobile ? 40 : 60, color: folder.color || 'primary.main' }} />
                                        <Typography variant="caption" sx={{ mt: 1, textAlign: 'center', wordBreak: 'break-word' }}>
                                            {folder.name}
                                        </Typography>
                                    </FolderBox>
                                </StyledPaper>
                            </Tooltip>
                        </Grid>
                    ))}
                    {displayImages.map(image => {
                        const isSelected = selectedImageUrls.includes(image.url);
                        const urlLower = image.url?.toLowerCase().split('?')[0] || '';
                        const isVideo =
                          image.mediaType === 'video' ||
                          urlLower.endsWith('.mp4') ||
                          urlLower.endsWith('.webm');
                        return (
                            <Grid item key={image.id} xs={6} sm={4} md={3} lg={2}>
                                <StyledPaper
                                    ref={image.url === scrollTargetUrl ? targetImageRef : undefined}
                                    isSelected={isSelected}
                                    onClick={() => handleImageSelect(image.url)}
                                >
                                    <ImageBox>
                                        {isVideo ? (
                                            <>
                                                <Box
                                                    component="video"
                                                    src={image.url}
                                                    muted
                                                    preload="metadata"
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0, left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        bgcolor: '#000'
                                                    }}
                                                />
                                                <Box sx={{
                                                    position: 'absolute',
                                                    top: 0, left: 0, right: 0, bottom: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    bgcolor: 'rgba(0,0,0,0.3)',
                                                }}>
                                                    <PlayCircleOutlineIcon sx={{ fontSize: 40, color: 'white' }} />
                                                </Box>
                                            </>
                                        ) : (
                                            <StyledImage src={getImageDisplayUrl(image)} alt={image.name} loading="lazy"/>
                                        )}
                                        {isSelected && (
                                            <Box sx={{
                                                position: 'absolute',
                                                top: 5,
                                                right: 5,
                                                color: 'white',
                                                backgroundColor: 'primary.main',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 24,
                                                height: 24
                                            }}>
                                                <CheckCircleIcon fontSize="small" />
                                            </Box>
                                        )}
                                    </ImageBox>
                                    <Tooltip title={image.name} placement="top">
                                        <Typography variant="caption" noWrap sx={{ display: 'block', mt: 1 }}>
                                            {image.name}
                                        </Typography>
                                    </Tooltip>
                                </StyledPaper>
                            </Grid>
                        )
                    })}
                    {displayFolders.length === 0 && displayImages.length === 0 && (
                        <Grid item xs={12}>
                            <Typography sx={{textAlign: 'center', p: 4, color: 'text.secondary'}}>
                                {t('imageLibraryModal.emptyFolder')}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            )}
        </DialogContent>
        <DialogActions>
            <Typography sx={{mr: 'auto', pl: 2}} color="text.secondary">
                {t('imageLibraryModal.selectionCount', { selected: selectedImageUrls.length, max: maxSelection })}
            </Typography>
            <Button onClick={handleClose}>{t('imageLibraryModal.actions.cancel')}</Button>
            <Button onClick={handleSave} variant="contained">
                {t('imageLibraryModal.actions.saveSelection')}
            </Button>
        </DialogActions>
         <Snackbar
            open={!!snackbar}
            autoHideDuration={4000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
            <Alert onClose={handleSnackbarClose} severity={snackbar?.severity || 'info'} sx={{ width: '100%' }}>
                {snackbar?.message}
            </Alert>
        </Snackbar>
        <ImageUploadModal
          open={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          companyId={companyId}
          selectedFolder={selectedFolder ? { id: selectedFolder.id, name: selectedFolder.name } : null}
          onUploadComplete={async () => {
            await loadData();
          }}
        />
        <VideoUploadModal
          open={isVideoUploadModalOpen}
          onClose={() => setIsVideoUploadModalOpen(false)}
          companyId={companyId}
          selectedFolder={selectedFolder ? { id: selectedFolder.id, name: selectedFolder.name } : null}
          onUploadComplete={async () => {
            await loadData();
          }}
        />
    </Dialog>
  );
};

export default ImageLibrarySelectionModal; 